// LargeAnchorScan — the full-screen "SCAN LOCATION" prompt that opens a
// Large order. Large waves are one-order-per-wave: the picker anchors
// every order by scanning the location they are physically standing at,
// and the BE builds a wave around the closest eligible order picked
// strictly forward (wrap-around) from that point.
//
// Used from two places with identical mechanics:
//   • PickTypeSelector — tapping the Large card lands here for the
//     FIRST order (no wave exists until the scan succeeds).
//   • ActiveWaveScreen's "next-order" phase — after a wave completes,
//     the picker is asked to scan wherever they NOW stand to open the
//     next order, or tap Done to stop.
//
// The parent owns the POST /picking/wave/start call and navigation; this
// component owns the scan capture and the per-outcome feedback:
//   started   → parent navigates; keep the overlay up (no flicker)
//   no_orders → "no more orders" beat, then auto-exit via onExit
//   rejected  → red flash + error audio, stay for a retry
//   failed    → red flash + error audio, stay (network — retryable)
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, PackageCheck } from "lucide-react";
import HiddenScanInput from "../HiddenScanInput";
import ScanPromptHero from "../picking-shared/ScanPromptHero";
import ScanFlashOverlay from "../ScanFlashOverlay";
import { playSuccess, playError } from "../helpers/audioPlayer";
import { useTranslation } from "../../i18n";

export type AnchorScanOutcome =
    | { kind: "started" }
    | { kind: "no_orders" }
    // Orders are queued but none fit the space left on the picker's
    // york. The parent routes to the drop-off screen; this component
    // never renders it, so it needs no feedback beat of its own.
    | { kind: "york_full" }
    | { kind: "rejected"; message?: string }
    | { kind: "failed" };

interface Props {
    /** Already-translated prompt for the resting hero. */
    prompt: string;
    /** Resolve with the outcome; the component renders the feedback. */
    onScan: (barcode: string) => Promise<AnchorScanOutcome>;
    /** Leave without opening an order (back button, Done, no-orders). */
    onExit: () => void;
    /** Already-translated label for the exit button. */
    exitLabel: string;
}

const FLASH_MS = 900;
const NO_ORDERS_MS = 1800;

const LargeAnchorScan = ({ prompt, onScan, onExit, exitLabel }: Props) => {
    const { t } = useTranslation();
    const [scanValue, setScanValue] = useState("");
    const [busy, setBusy] = useState(false);
    const [flash, setFlash] = useState<{ active: boolean; message?: string }>({ active: false });
    const [noOrders, setNoOrders] = useState(false);
    const flashTimer = useRef<number | null>(null);
    const exitTimer = useRef<number | null>(null);
    // A "started" outcome unmounts us via the parent's navigation —
    // don't let the in-flight promise's setState land after that.
    const unmountedRef = useRef(false);
    useEffect(() => () => {
        unmountedRef.current = true;
        if (flashTimer.current !== null) window.clearTimeout(flashTimer.current);
        if (exitTimer.current !== null) window.clearTimeout(exitTimer.current);
    }, []);

    const submit = useCallback(async (value?: string) => {
        const barcode = (value ?? "").trim();
        setScanValue("");
        if (!barcode || busy || noOrders) return;
        setBusy(true);
        let outcome: AnchorScanOutcome;
        try {
            outcome = await onScan(barcode);
        } catch {
            outcome = { kind: "failed" };
        }
        if (unmountedRef.current) return;
        if (outcome.kind === "started") {
            playSuccess();
            // Stay busy — the parent's refetch/navigation replaces this
            // screen; re-enabling scans here would double-start.
            return;
        }
        if (outcome.kind === "york_full") {
            // A good scan with a full york. The parent swaps us for the
            // drop-off screen, so stay busy rather than flashing red —
            // nothing the picker did was wrong.
            playSuccess();
            return;
        }
        if (outcome.kind === "no_orders") {
            setNoOrders(true);
            exitTimer.current = window.setTimeout(onExit, NO_ORDERS_MS);
            return;
        }
        playError();
        setFlash({ active: true, message: outcome.kind === "rejected" ? outcome.message : undefined });
        if (flashTimer.current !== null) window.clearTimeout(flashTimer.current);
        flashTimer.current = window.setTimeout(() => setFlash({ active: false }), FLASH_MS);
        setBusy(false);
    }, [busy, noOrders, onScan, onExit]);

    return (
        <div className="scan-panel picking-active picking-anchor-scan">
            <div className="picking-anchor-topbar">
                <button
                    type="button"
                    className="btn btn-ghost picking-anchor-exit"
                    onClick={onExit}
                    disabled={busy && !noOrders}
                >
                    <ArrowLeft size={18} aria-hidden="true" />
                    {exitLabel}
                </button>
            </div>
            {noOrders ? (
                <div className="picking-complete">
                    <div className="picking-wave-complete">
                        <PackageCheck className="picking-wave-complete-icon" strokeWidth={1.5} />
                        <div className="picking-wave-complete-msg">{t("picking_anchor_no_orders")}</div>
                    </div>
                </div>
            ) : (
                <ScanPromptHero prompt={prompt} flashHidden={flash.active} />
            )}
            <ScanFlashOverlay active={flash.active} message={flash.message} />
            <HiddenScanInput
                value={scanValue}
                onChange={setScanValue}
                onSubmit={submit}
                disabled={busy || noOrders}
                disableCamera
                hideToggles
            />
        </div>
    );
};

export default LargeAnchorScan;
